import { useState } from 'react';
import { Link } from 'react-router-dom';

const Step = ({ number, icon, title, desc, badge }) => (
  <div className="d-flex gap-3 mb-4">
    <div className="flex-shrink-0 d-flex flex-column align-items-center">
      <div
        className="d-flex align-items-center justify-content-center rounded-circle fw-bold text-white bg-primary"
        style={{ width: 40, height: 40, fontSize: 15 }}
      >
        {number}
      </div>
      <div
        className="flex-grow-1 border-start border-2 border-primary-subtle"
        style={{ width: 2, minHeight: 24, marginTop: 4 }}
      ></div>
    </div>
    <div className="pb-2" style={{ flex: 1 }}>
      <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
        <i className={`bi ${icon} text-primary`} style={{ fontSize: 16 }}></i>
        <span className="fw-semibold" style={{ fontSize: 15 }}>
          {title}
        </span>
        {badge && (
          <span className={`badge rounded-pill ${badge.color}`} style={{ fontSize: 11 }}>
            {badge.label}
          </span>
        )}
      </div>
      <p className="text-muted mb-0" style={{ fontSize: 13, lineHeight: 1.6 }}>
        {desc}
      </p>
    </div>
  </div>
);

const guestSteps = [
  {
    icon: 'bi-person-plus',
    title: 'Créer un compte',
    desc: "Inscription avec email ou Google. Upload du permis de conduire et pièce d'identité obligatoires pour réserver.",
  },
  {
    icon: 'bi-search',
    title: 'Rechercher un véhicule',
    desc: 'Saisir la localisation, les dates (date + heure de début / fin) et filtrer par catégorie, prix, options.',
  },
  {
    icon: 'bi-calendar-check',
    title: 'Demander la réservation',
    desc: "Envoyer une demande au propriétaire. La réservation est en statut « En attente » jusqu'à acceptation.",
    badge: { label: 'pending', color: 'bg-warning-subtle text-warning border border-warning' },
  },
  {
    icon: 'bi-credit-card',
    title: 'Payer',
    desc: 'Après acceptation, enregistrer sa carte bancaire. Le paiement est capturé immédiatement. La location passe en « Confirmée ».',
    badge: { label: 'paid', color: 'bg-primary-subtle text-primary border border-primary' },
  },
  {
    icon: 'bi-car-front',
    title: 'Check-in',
    desc: "Le jour J, le propriétaire ou le conducteur remplit l'état des lieux de départ (km, carburant, photos). La location passe « En cours ».",
    badge: { label: 'in_progress', color: 'bg-success-subtle text-success border border-success' },
  },
  {
    icon: 'bi-flag-fill',
    title: 'Retour du véhicule (checkout)',
    desc: "Le conducteur renseigne l'état des lieux de retour. Le propriétaire valide. Les km supplémentaires sont prélevés automatiquement si besoin.",
  },
  {
    icon: 'bi-check2-circle',
    title: 'Location terminée',
    desc: 'Le statut passe à « Terminée ». Le dépôt de garantie est libéré sous 72 h. Les deux parties peuvent laisser un avis.',
    badge: { label: 'completed', color: 'bg-success-subtle text-success border border-success' },
  },
];

const hostSteps = [
  {
    icon: 'bi-person-plus',
    title: 'Créer un compte hôte',
    desc: 'Inscription + onboarding Stripe Connect pour recevoir les virements. Les documents du véhicule (carte grise, assurance, CT) sont requis.',
  },
  {
    icon: 'bi-car-front',
    title: 'Ajouter un véhicule',
    desc: "Saisir le modèle, les photos, la description, les options disponibles et l'adresse de remise.",
  },
  {
    icon: 'bi-currency-euro',
    title: "Configurer l'annonce",
    desc: "Définir le prix par jour, le kilométrage inclus, les horaires de remise/retour, le délai de préavis et les conditions d'annulation.",
  },
  {
    icon: 'bi-calendar3',
    title: 'Gérer les disponibilités',
    desc: "Configurer les créneaux disponibles par jour de la semaine, et bloquer les périodes d'indisponibilité.",
  },
  {
    icon: 'bi-inbox',
    title: 'Recevoir et accepter les demandes',
    desc: "Consulter les demandes entrantes dans la messagerie. Vérifier les documents du conducteur avant d'accepter ou refuser.",
    badge: {
      label: 'action requise',
      color: 'bg-warning-subtle text-warning border border-warning',
    },
  },
  {
    icon: 'bi-clipboard-check',
    title: 'Check-in — état des lieux départ',
    desc: "Photographier le véhicule, relever le kilométrage et le niveau de carburant, valider le check-in dans l'application.",
  },
  {
    icon: 'bi-clipboard2-check',
    title: 'Checkout — état des lieux retour',
    desc: 'Vérifier le véhicule au retour. En cas de dommages, ouvrir un litige. Sinon, valider le checkout pour déclencher le paiement.',
  },
  {
    icon: 'bi-bank',
    title: 'Recevoir le virement',
    desc: 'Le virement est initié automatiquement par Stripe Connect 24 h après le checkout validé (hors litige). Commission plateforme déduite.',
    badge: { label: '–15% commission', color: 'bg-secondary-subtle text-secondary' },
  },
];

export default function HowItWorksPage() {
  const [activeTab, setActiveTab] = useState('guest');

  return (
    <div className="bg-body-tertiary min-vh-100 py-5">
      <div className="container" style={{ maxWidth: 820 }}>
        {/* Header */}
        <div className="text-center mb-5">
          <span
            className="badge bg-success-subtle text-success rounded-pill px-3 py-2 mb-3"
            style={{ fontSize: 13 }}
          >
            Guide utilisateur
          </span>
          <h1 className="fw-bold mb-2">Comment ça marche ?</h1>
          <p className="text-muted" style={{ maxWidth: 540, margin: '0 auto' }}>
            Découvrez le parcours complet — du côté conducteur et du côté propriétaire — pour une
            location réussie sur Moovicar.
          </p>
        </div>

        {/* Onglets */}
        <div className="d-flex gap-2 justify-content-center mb-4">
          <button
            className={`btn rounded-pill px-4 fw-semibold ${activeTab === 'guest' ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setActiveTab('guest')}
          >
            <i className="bi bi-person me-2"></i>Conducteur
          </button>
          <button
            className={`btn rounded-pill px-4 fw-semibold ${activeTab === 'host' ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setActiveTab('host')}
          >
            <i className="bi bi-key me-2"></i>Propriétaire
          </button>
        </div>

        {/* Contenu */}
        <div className="card border-0 shadow-sm rounded-4 p-4 p-md-5">
          {activeTab === 'guest' ? (
            <>
              <div className="d-flex align-items-center gap-3 mb-4">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle"
                  style={{ width: 48, height: 48 }}
                >
                  <i className="bi bi-person-fill text-primary" style={{ fontSize: 22 }}></i>
                </div>
                <div>
                  <h4 className="fw-bold mb-0">Parcours conducteur</h4>
                  <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                    De l'inscription à la fin de location
                  </p>
                </div>
              </div>
              {guestSteps.map((s, i) => (
                <Step key={i} number={i + 1} {...s} />
              ))}
            </>
          ) : (
            <>
              <div className="d-flex align-items-center gap-3 mb-4">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle bg-success-subtle"
                  style={{ width: 48, height: 48 }}
                >
                  <i className="bi bi-house-fill text-success" style={{ fontSize: 22 }}></i>
                </div>
                <div>
                  <h4 className="fw-bold mb-0">Parcours propriétaire</h4>
                  <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                    De l'inscription au virement
                  </p>
                </div>
              </div>
              {hostSteps.map((s, i) => (
                <Step key={i} number={i + 1} {...s} />
              ))}
            </>
          )}
        </div>

        {/* Récap chiffres clés */}
        <div className="row g-3 mt-2">
          {[
            { icon: 'bi-percent', value: '15 %', label: 'Commission hôte', color: 'text-primary' },
            {
              icon: 'bi-clock-history',
              value: '72 h',
              label: 'Libération garantie',
              color: 'text-success',
            },
            {
              icon: 'bi-speedometer2',
              value: '0,13 €',
              label: 'Par km supplémentaire',
              color: 'text-warning',
            },
            {
              icon: 'bi-calendar-x',
              value: '48 h',
              label: 'Annulation sans frais',
              color: 'text-danger',
            },
          ].map(({ icon, value, label, color }) => (
            <div key={label} className="col-6 col-md-3">
              <div className="card border-0 shadow-sm rounded-4 p-3 text-center h-100">
                <i className={`bi ${icon} ${color} mb-1`} style={{ fontSize: 22 }}></i>
                <p className={`fw-bold mb-0 ${color}`} style={{ fontSize: 22 }}>
                  {value}
                </p>
                <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                  {label}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer nav */}
        <div className="d-flex flex-wrap justify-content-center gap-2 mt-4">
          <Link to="/regles-business" className="btn btn-outline-primary rounded-3 btn-sm">
            <i className="bi bi-file-text me-1"></i>Règles business
          </Link>
          <Link to="/etats-reservation" className="btn btn-outline-secondary rounded-3 btn-sm">
            <i className="bi bi-diagram-3 me-1"></i>États de réservation
          </Link>
        </div>
      </div>
    </div>
  );
}
